import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, filter, Subject, takeUntil } from 'rxjs';
import { AcademyCertificateId, AcademyLesson, certificateById } from '../../../academy/academy-catalog';
import { AcademyQuestion, AcademyService, CertificateState, LessonStatus } from '../../../academy/academy.service';
import { AcademyQuizResult } from '../../../components/academy/academy-quiz.component';
import { ORBI_IDLE } from '../../../components/orbi-lab/orbi-lab.component';

interface MissingRow {
  lesson: AcademyLesson;
  title: string;
  subtitle: string;
  status: LessonStatus;
}

/** Orbi freut sich über ein bestandenes Zertifikat. */
const CELEBRATE_SCRIPT = ['hop-laugh', ORBI_IDLE, 'giggle-bounce', ORBI_IDLE, ORBI_IDLE];

/**
 * Ein Zertifikat des AlphaLearn Führerscheins (`/academy/certificates/:id`):
 * Voraussetzungen (Vorgänger-Zertifikat, Lektionen der Themen) und der
 * Abschlusstest — Fragen zufällig aus den Quizfragen der Prüfungsthemen,
 * bestanden ab `passPercent`. Ein bestandener Test bleibt bestanden; ein
 * neuer Versuch kann die beste Punktzahl nur verbessern.
 */
@Component({
  selector: 'app-academy-certificate',
  templateUrl: './academy-certificate.component.html',
  styleUrls: ['./academy-certificate.component.scss'],
})
export class AcademyCertificateComponent implements OnInit, OnDestroy {
  state: CertificateState | null = null;
  title = '';
  description = '';
  prerequisiteTitle = '';
  missing: MissingRow[] = [];
  exam: AcademyQuestion[] | null = null;
  result: (AcademyQuizResult & { passed: boolean }) | null = null;
  ready = false;
  readonly celebrateScript = CELEBRATE_SCRIPT;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private academy: AcademyService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.academy.ensureContent();
    this.academy.loadProgress();
    combineLatest([
      this.route.paramMap,
      this.academy.content$.pipe(filter((c) => !!c)),
      this.academy.progress$,
      this.academy.progressLoaded$.pipe(filter(Boolean)),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([params]) => {
        const id = params.get('certificateId') ?? '';
        if (!certificateById(id)) {
          this.router.navigate(['/academy']);
          return;
        }
        if (this.state && this.state.cert.id !== id) this.cancelExam();
        this.build(id as AcademyCertificateId);
        this.ready = true;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  startExam(): void {
    if (!this.state?.examUnlocked) return;
    this.result = null;
    this.exam = this.academy.drawExam(this.state.cert);
  }

  cancelExam(): void {
    this.exam = null;
    this.result = null;
  }

  onExamCompleted(result: AcademyQuizResult): void {
    if (!this.state) return;
    const passed = result.percent >= this.state.cert.passPercent;
    this.result = { ...result, passed };
    this.academy.recordExam(this.state.cert.id, result.percent, passed);
  }

  trackMissing(_: number, r: MissingRow): string {
    return r.lesson.id;
  }

  private build(id: AcademyCertificateId): void {
    const state = this.academy.certificateState(id);
    if (!state) return;
    this.state = state;
    const text = this.academy.certificateText(id);
    this.title = text.title;
    this.description = text.description;
    this.prerequisiteTitle = state.cert.requires ? this.academy.certificateText(state.cert.requires).title : '';
    this.missing = state.lessons
      .filter((l) => this.academy.lessonStatus(l.id) !== 'completed')
      .map((lesson) => ({
        lesson,
        title: this.academy.lessonTitle(lesson.id),
        subtitle: this.academy.topicText(lesson.topic).title,
        status: this.academy.lessonStatus(lesson.id),
      }));
  }
}
